import React, { useState, useEffect } from 'react';
import {
  Space,
  SearchBar,
  Badge,
  DotLoading,
  AutoCenter,
  ErrorBlock,
} from 'antd-mobile';
import { Avatar } from 'antd';
import { CarOutlined } from '@ant-design/icons';

const generateAvatarPath = (name) => {
  const hash = Array.from(name).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0
  );
  const avatarNum = (hash % 7) + 1; // 生成1到7的数字
  return `/images/picture/avatar-0${avatarNum}.svg`;
};

const ChatContainer = ({
  name,
  carno,
  image,
  time,
  message,
  unreadMessages,
  isOnline,
  onClick,
}) => {
  const [avatarSrc, setAvatarSrc] = useState('');

  // 車牌數量
  const CarQuantityBadge = () => {
    const content = carno.length;
    const color = 'darkgrey';
    return (
      <Space style={{ '--gap': '24px', margin: '7px 6px 0px -5px' }}>
        <Badge color={color} content={content} style={{ '--right': '10%' }}>
          <div style={{ margin: '6px' }}>
            <CarOutlined
              style={{ fontSize: '18px', color: color }}></CarOutlined>
          </div>
        </Badge>
      </Space>
    );
  };

  // 在線、離線
  const IsOnlineBadge = () => {
    const content = isOnline ? '在線' : '離線';
    const color = isOnline ? '#87D068' : '#EEEEEE';
    return (
      <Badge
        color={color}
        content={content}
        style={{ marginLeft: '8px', fontSize: '60px' }}
      />
    );
  };

  const convertCustomTime = (datatimeStr) => {
    if (!datatimeStr) {
      return '';
    }
    const originalDate = new Date(datatimeStr);
    const taiwanDate = new Date(originalDate.getTime() + 8 * 3600000);
    return taiwanDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const displayUnreadMessages = unreadMessages > 99 ? '99+' : unreadMessages;

  useEffect(() => {
    setAvatarSrc(generateAvatarPath(name));
  }, [name]); // 依赖于name，确保name变化时更新头像，如果name固定，则头像不变

  return (
    <div className="chat-person-container" onClick={onClick}>
      <div className="avatar-wrapper">
        <Avatar src={image ? image : avatarSrc} size={56} />
      </div>
      <div className="chat-content">
        <div className="chat-row mb-1">
          <div className="message-length text-body font-semibold">
            <CarQuantityBadge />
            <span>{name}</span>
            <IsOnlineBadge />
          </div>
          <div className="chat-time text-sm font-normal">
            {convertCustomTime(time)}
          </div>
        </div>
        <div className="chat-row">
          <div className="message-length text-sm font-normal">{message}</div>
          {unreadMessages > 0 && (
            <div className="message-num">{displayUnreadMessages}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatList = ({
  isLoading,
  me,
  friends,
  startChat,
  plateNumber,
  setIsForceFriend,
}) => {
  const [searchKeyword, setSearchKeyword] = useState(''); // 搜尋關鍵字
  const [filteredFriends, setFilteredFriends] = useState([]); // 好友資料陣列
  const [avatarSrc, setAvatarSrc] = useState(''); // 預設大頭貼

  // 前往與這個好友的聊天室
  const gotoChatroom = (friend) => {
    startChat(friend);
  };

  useEffect(() => {
    setAvatarSrc(generateAvatarPath('default'));

    let filtered = friends;

    // 根據搜尋關鍵字過濾好友清單
    if (searchKeyword.trim().length > 0) {
      filtered = Object.keys(friends).reduce((acc, key) => {
        const friend = friends[key];
        if (friend.name.toLowerCase().includes(searchKeyword.toLowerCase())) {
          acc[key] = friend;
        }
        return acc;
      }, {});
    }

    // 根據最後一筆訊息時間 新->舊 排序
    const filteredArray = Object.values(filtered);
    filteredArray.sort((a, b) => {
      const dateA = new Date(a.latest_message_time);
      const dateB = new Date(b.latest_message_time);
      return dateB - dateA;
    });

    setFilteredFriends(filteredArray);

    // 若是有傳入車牌號碼，則找到相對應的車牌號碼好友
    // 然後直接進入聊天室，不顯示好友清單
    if (plateNumber.trim().length > 0) {
      const matchedCarno = Object.values(friends).find((innerObj) =>
        innerObj.carno.includes(plateNumber)
      );
      if (matchedCarno) {
        gotoChatroom(matchedCarno);
        setIsForceFriend(true);
      }
    }
  }, [friends, searchKeyword]);

  return (
    <div className="main-container">
      {/* 聊天室 */}
      {isLoading ? (
        <AutoCenter>
          正在載入聊天室
          <DotLoading />
        </AutoCenter>
      ) : (
        <Space block direction="vertical" style={{ '--gap': '16px' }}>
          <div className="subheading font-bold pd-1 pl-3">
            <Avatar
              src={
                me.user_info.profile_image_url
                  ? me.user_info.profile_image_url
                  : avatarSrc
              }
              size={56}
            />
            <span className="pl-3">{me.user_info.name}</span>
          </div>
          <SearchBar
            className="custom-search-bar"
            placeholder="搜尋"
            showCancelButton
            onChange={(value) => {
              setSearchKeyword(value);
            }}
            style={{
              '--border-radius': '8px',
              '--background': '#ffffff',
              '--height': '48px',
              '--padding-left': '12px',
            }}
          />
          {filteredFriends.length === 0 ? (
            <AutoCenter>
              <ErrorBlock
                status="empty"
                title={<>查無好友對話</>}
                description={<></>}
                style={{ '--image-height': '200px', '--image-width': '200px' }}
              />
            </AutoCenter>
          ) : (
            <Space block direction="vertical" style={{ '--gap': '8px' }}>
              {filteredFriends.map((item, index) => {
                return (
                  <ChatContainer
                    key={item.user_id}
                    onClick={() => gotoChatroom(item)}
                    name={item.name}
                    carno={item.carno}
                    image={item.profile_image_url}
                    time={item.latest_message_time}
                    message={item.latest_message}
                    unreadMessages={item.unread_count}
                    isOnline={item.is_online}
                  />
                );
              })}
            </Space>
          )}
        </Space>
      )}
    </div>
  );
};

export default ChatList;
